import React, { useState } from 'react';
import { SectionHeader } from '../components/ui/SectionHeader';
import { UploadCloud, CheckCircle, AlertTriangle, Radio, ArrowRight } from 'lucide-react';
import { SEO } from '../components/SEO';
import { sendForm } from '../services/formService';

export const DemoSubmission: React.FC = () => {
  const [formData, setFormData] = useState({
    artistName: '',
    email: '',
    trackTitle: '',
    scLink: '',
    genre: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    
    const { success, error } = await sendForm('demo-submission', formData);

    if (success) {
      setStatus('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      alert("Error submitting demo: " + error);
      setStatus('idle');
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center container mx-auto px-6 py-12">
        <SEO title="Demo Submitted" description="Your demo has been transmitted to AstralVibe." />
        <div className="bg-white/10 border border-astral-purple/30 p-8 md:p-12 text-center max-w-2xl w-full relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-astral-purple opacity-20 blur-[50px]"></div>
          
          <div className="mb-6 flex justify-center">
             <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center border border-green-500/50">
                <CheckCircle className="w-10 h-10 text-green-500" />
             </div>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-display font-bold uppercase text-white mb-4">Signal Received</h2>
          <p className="text-slate-200 text-base md:text-lg mb-8 leading-relaxed">
            Your transmission has been logged in our mainframe. <br/>
            Our A&R team analyzes frequencies weekly. If your vibration aligns with the AstralVibe, we will establish contact.
          </p>
          
          <button 
            onClick={() => {
              setFormData({ artistName: '', email: '', trackTitle: '', scLink: '', genre: '' });
              setStatus('idle');
            }}
            className="px-8 py-3 bg-white text-black font-bold uppercase tracking-widest text-xs hover:bg-astral-purple hover:text-white transition-colors"
          >
            Transmit Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <SEO 
        title="Submit Demos" 
        description="Send your best unreleased tracks to AstralVibe. We are looking for high-quality Afro House and Techno." 
        url="/demo"
        keywords={["demo submission", "submit music", "techno label demo", "afro house demo", "music label submission"]}
      />
      <div className="container mx-auto px-6">
        <SectionHeader subtitle="Demos" title="Submit Your Frequency" />

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          
          {/* Left Column: Guidelines */}
          <div className="lg:col-span-5 space-y-12">
            <div>
              <p className="text-lg md:text-xl text-white font-light leading-relaxed mb-6">
                We are constantly scanning the horizon for the next evolution of sound. 
                Before transmitting your demo, ensure it aligns with our protocol.
              </p>
            </div>

            <div className="space-y-8">
              <div className="bg-white/10 border border-white/10 p-6 relative group hover:border-astral-purple/50 transition-colors">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-100 transition-opacity text-astral-purple">
                  <UploadCloud />
                </div>
                <h4 className="text-white font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
                  <Radio className="w-4 h-4 text-astral-cyan" /> 
                  Submission Protocol
                </h4>
                <ul className="space-y-3 text-sm text-slate-300 mt-4">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-astral-purple rounded-full mt-1.5 shrink-0"></span>
                    <span>SoundCloud <strong>Private Links</strong> only.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-astral-purple rounded-full mt-1.5 shrink-0"></span>
                    <span>Downloads must be enabled.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-astral-purple rounded-full mt-1.5 shrink-0"></span>
                    <span>MP3 320kbps or WAV preferred.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 bg-astral-purple rounded-full mt-1.5 shrink-0"></span>
                    <span>Do not submit unfinished drafts or snippets.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white/10 border border-white/10 p-6 group hover:border-red-500/30 transition-colors">
                 <h4 className="text-white font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" /> 
                  Warning
                </h4>
                <p className="text-sm text-slate-300 leading-relaxed">
                  We do not accept bootlegs, mashups, or copyright-infringing material. 
                  Original compositions only. If we are interested, you will hear from us within 14 days.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Form */}
          <div className="lg:col-span-7">
            <div className="bg-black/60 backdrop-blur-sm border border-white/10 p-8 md:p-12 relative">
               {/* Decorative elements */}
               <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-astral-purple to-transparent opacity-50"></div>
               
               <form name="demo-submission" onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-2 group">
                       <label className="text-xs font-mono text-astral-cyan uppercase tracking-widest">Artist Name</label>
                       <input 
                          required
                          type="text" 
                          name="artistName"
                          value={formData.artistName}
                          onChange={(e) => setFormData({...formData, artistName: e.target.value})}
                          className="w-full bg-transparent border-b border-white/20 py-3 text-white text-lg focus:border-astral-purple focus:outline-none transition-all placeholder-slate-400"
                          placeholder="Ex: VOID WALKER"
                       />
                    </div>
                    <div className="space-y-2 group">
                       <label className="text-xs font-mono text-astral-cyan uppercase tracking-widest">Email Address</label>
                       <input 
                          required
                          type="email" 
                          name="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="w-full bg-transparent border-b border-white/20 py-3 text-white text-lg focus:border-astral-purple focus:outline-none transition-all placeholder-slate-400"
                          placeholder="contact@artist.com"
                       />
                    </div>
                  </div>

                  <div className="space-y-2 group">
                       <label className="text-xs font-mono text-astral-cyan uppercase tracking-widest">Track Title(s)</label>
                       <input 
                          required
                          type="text" 
                          name="trackTitle"
                          value={formData.trackTitle}
                          onChange={(e) => setFormData({...formData, trackTitle: e.target.value})}
                          className="w-full bg-transparent border-b border-white/20 py-3 text-white text-lg focus:border-astral-purple focus:outline-none transition-all placeholder-slate-400"
                          placeholder="Enter track names..."
                       />
                  </div>

                  <div className="space-y-2 group">
                       <label className="text-xs font-mono text-astral-cyan uppercase tracking-widest">SoundCloud Private Link</label>
                       <div className="relative">
                          <input 
                              required
                              type="url" 
                              name="scLink"
                              value={formData.scLink}
                              onChange={(e) => setFormData({...formData, scLink: e.target.value})}
                              className="w-full bg-transparent border-b border-white/20 py-3 text-white text-lg focus:border-astral-purple focus:outline-none transition-all placeholder-slate-400 pr-10"
                              placeholder="https://soundcloud.com/..."
                          />
                          <UploadCloud className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                       </div>
                       <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-1">Make sure the link is accessible and downloadable</p>
                  </div>

                  <div className="space-y-2 group">
                       <label className="text-xs font-mono text-astral-cyan uppercase tracking-widest">Genre / Style</label>
                       <select 
                          name="genre"
                          value={formData.genre}
                          onChange={(e) => setFormData({...formData, genre: e.target.value})}
                          className="w-full bg-black border-b border-white/20 py-3 text-white text-lg focus:border-astral-purple focus:outline-none transition-all"
                       >
                          <option value="">Select Genre...</option>
                          <option value="melodic-techno">Melodic Techno</option>
                          <option value="afro-house">Afro House</option>
                          <option value="minimal">Minimal / Deep Tech</option>
                          <option value="progressive">Progressive House</option>
                          <option value="other">Experimental / Other</option>
                       </select>
                  </div>

                  <div className="pt-8">
                    <button 
                      type="submit" 
                      disabled={status === 'submitting'}
                      className="w-full group relative overflow-hidden bg-white text-black py-5 font-display font-bold uppercase tracking-[0.2em] hover:bg-astral-purple hover:text-white transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="relative z-10 flex items-center justify-center gap-3">
                        {status === 'submitting' ? 'Uploading Signal...' : 'Initiate Upload'} 
                        {status !== 'submitting' && <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />}
                      </span>
                      {status === 'submitting' && (
                        <div className="absolute inset-0 bg-astral-purple/20 w-full h-full animate-pulse"></div>
                      )}
                    </button>
                  </div>
               </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};